/**
 * Dialog360 Profile Tab
 * View and edit the WhatsApp business profile (360dialog).
 */

import { useEffect } from 'react';
import { useForm } from '@mantine/form';
import { IconRefresh, IconDeviceFloppy } from '@tabler/icons-react';
import { Stack, Group, Button, Text, Title, Card, TextInput, Textarea } from '../../../../../components/ui';
import { useDialog360 } from '../hooks/useDialog360';
import type { BusinessProfile } from '../dialog360.types';

export function Dialog360ProfileTab() {
  const { loading, profile, fetchProfile, updateProfile } = useDialog360();

  const form = useForm({
    initialValues: {
      about: '',
      address: '',
      description: '',
      email: '',
      vertical: '',
      websites: '',
      profilePictureHandle: '',
    },
  });

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (profile) {
      form.setValues({
        about: profile.about ?? '',
        address: profile.address ?? '',
        description: profile.description ?? '',
        email: profile.email ?? '',
        vertical: profile.vertical ?? '',
        websites: profile.websites?.join(', ') ?? '',
        profilePictureHandle: profile.profilePictureHandle ?? '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const handleSubmit = async (values: typeof form.values) => {
    const payload: BusinessProfile = {
      about: values.about,
      address: values.address,
      description: values.description,
      email: values.email,
      vertical: values.vertical,
      profilePictureHandle: values.profilePictureHandle,
      websites: values.websites
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    };
    await updateProfile(payload);
  };

  return (
    <Stack gap="md">
      <Card>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Title order={4}>Business Profile</Title>
                <Text size="sm" c="dimmed">
                  View and update the public WhatsApp business profile served by 360dialog
                </Text>
              </div>
              <Button
                variant="subtle"
                leftSection={<IconRefresh size={16} />}
                onClick={() => fetchProfile()}
                loading={loading}
              >
                Refresh
              </Button>
            </Group>

            <Textarea
              label="About"
              placeholder="Short business tagline shown on your profile"
              rows={2}
              {...form.getInputProps('about')}
            />

            <TextInput
              label="Address"
              placeholder="123 Main Street, City"
              {...form.getInputProps('address')}
            />

            <Textarea
              label="Description"
              placeholder="A longer description of your business"
              rows={4}
              {...form.getInputProps('description')}
            />

            <TextInput
              label="Email"
              placeholder="contact@example.com"
              {...form.getInputProps('email')}
            />

            <TextInput
              label="Vertical"
              placeholder="e.g. NONPROFIT, EDU, RETAIL"
              {...form.getInputProps('vertical')}
            />

            <TextInput
              label="Websites (comma-separated)"
              placeholder="https://example.com, https://example.org"
              {...form.getInputProps('websites')}
            />

            <TextInput
              label="Profile Picture Handle"
              placeholder="Uploaded media handle for the profile picture"
              {...form.getInputProps('profilePictureHandle')}
            />

            <Group justify="flex-end">
              <Button
                type="submit"
                leftSection={<IconDeviceFloppy size={16} />}
                loading={loading}
                gradientVariant="primary"
              >
                Save Profile
              </Button>
            </Group>
          </Stack>
        </form>
      </Card>
    </Stack>
  );
}
